import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import ConfirmDialog from "../../components/ConfirmDialog";
import { isBlank, notBlank } from "../../util/ext";
import Paper from "@material-ui/core/Paper";
import { useConfirm } from "../../common/use-confirm";
import {
  any,
  append,
  concat,
  difference,
  map,
  prop,
  propEq,
  reject,
  sortBy,
  without,
} from "ramda";
import Grid from "@material-ui/core/Grid";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import DialogContentText from "@material-ui/core/DialogContentText";
import { useContributorRoles } from "../cspServices";
import { caseInsensitiveSort } from "../../util/sorts";

const useStyles = makeStyles(theme => ({
  form: {
    marginTop: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const sortContributorRoles = caseInsensitiveSort("display");

const ContributorRolesDialog = ({
  open,
  onCancel,
  onApply,
  submissionType,
}) => {
  const classes = useStyles();

  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);

  const contributorRolesResponse = useContributorRoles();
  useEffect(() => {
    if (contributorRolesResponse.isSuccess) {
      if (notBlank(submissionType.contributorRoles)) {
        setSelected(submissionType.contributorRoles);
        const unused = reject(
          role =>
            any(propEq("name", role.name), submissionType.contributorRoles),
          contributorRolesResponse.data,
        );

        setAvailable(
          sortBy(prop("name"), concat(submissionType.contributorRoles, unused)),
        );
      } else {
        setAvailable(contributorRolesResponse.data);
      }
    }
  }, [
    contributorRolesResponse.isSuccess,
    contributorRolesResponse.data,
    submissionType.contributorRoles,
  ]);

  const handleClose = useCallback(() => {
    const previous = notBlank(submissionType.contributorRoles)
      ? submissionType.contributorRoles
      : [];
    if (isBlank(difference(selected, previous))) {
      onCancel();
    } else {
      showConfirm(
        "Discard Changes?",
        "You have selected contributor roles without applying.  Do you wish to discard these changes?",
        {
          onConfirm: () => {
            onCancel();
          },
        },
      );
    }
  }, [selected, submissionType.contributorRoles, showConfirm, onCancel]);

  const handleApply = useCallback(() => {
    onApply(selected);
  }, [onApply, selected]);

  const handleSelected = role => event => {
    if (event.target.checked) {
      setSelected(prev => append(role, prev));
    } else {
      setSelected(prev => without([role], prev));
    }
  };

  return (
    <Dialog open={open} maxWidth="md">
      <DialogTitle id="add-submission-types-dialog-title">
        Update Contributor Roles
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {`Check the Contributor Roles that should allowed for ${submissionType.displayName} submissions`}
        </DialogContentText>
        <Paper>
          <Box className={classes.form}>
            <Grid spacing={2} container>
              {map(
                role => (
                  <Grid key={role.name} item md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={any(propEq("name", role.name), selected)}
                          onChange={handleSelected(role)}
                          name={role.name}
                          color="primary"
                        />
                      }
                      label={role.display}
                    />
                  </Grid>
                ),
                sortContributorRoles(available),
              )}
            </Grid>
          </Box>
        </Paper>
        <ConfirmDialog handler={confirmHandler} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContributorRolesDialog;
